import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { Role_Account_Query, Role_Management } from "../../../../../services/userAPI";
import { message } from "antd";
import toggleImage01 from "../../../../../Images/toggles-fill-image01.png";
import NextIcon from "../../../../../Images/nextIcon.png";
import TargetFunnelConfiguration from "./targetFunnelConfiguration";
import TargetFunnelPreview from "./targetFunnelPreview";
import LeadIndicatorSelection from "./leadIndicatorSelection";
import BackIcon from "../../../../../Images/backIcon.png";
import HomeIcon from "../../../../../Images/homeIcon.png";

import "./productConfiguration.css";
import PageBreadcrumb from '../../../../../reusableField/PageBreadcrumb';

const ProductConfiguration = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const dartsFormNeeded = location.state?.dartsFormNeeded;
    const accountId = location.state?.accountId;
    const roleId = location.state?.roleId;
    const pocName = location.state?.pocName;
    const pocEmail = location.state?.pocEmail;
    const pocNumber = location.state?.pocNumber;
    const numberLogin = location.state?.numberLogin;
    const emailLogin = location.state?.emailLogin;
    const mandatoryTimeDistribution = location.state?.mandatoryTimeDistribution;
    const targetEditDays = location.state?.targetEditDays;
    const inputEditDays = location.state?.inputEditDays;
    const name = location.state?.name;
    const dartsNeeded = location.state?.dartsNeeded;
    const clanNeeded = location.state?.clanNeeded;
    const syncNeeded = location.state?.syncNeeded;
    const accountName = location.state?.accountName;
    const geoTracking = location.state?.geoTracking;
    const signInSignOutRequired = location.state?.signInSignOutRequired;
    const locationRequired = location.state?.locationRequired;
    const jobroleAccessUserIds = location.state?.jobroleAccessUserIds;
    const ldmsNeeded = location.state?.ldmsNeeded;
    const mirrorNeeded = location.state?.mirrorNeeded;
    const roleNameState = location.state?.roleName;
    const [activeTab, setActiveTab] = useState("darts");
    const [inputType, setInputType] = useState("Forms");
    const [initialInputType, setInitialInputType] = useState("Forms");
    const [currentView, setCurrentView] = useState("main");
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [isSaveDisabled, setIsSaveDisabled] = useState(true);
    const [isNextDisabled, setIsNextDisabled] = useState(true);
    const [targetData, setTargetData] = useState(null);
    const [leadIndicatorData, setLeadIndicatorData] = useState(null);

    const safeRoleId = roleId;
    const safeAccountId = accountId;
    const safeAccountName = accountName;

    const [addJobRole] = useMutation(Role_Management);

    const { data: roleData } = useQuery(Role_Account_Query, {
        variables: { accountId: accountId ? parseInt(accountId) : null },
        fetchPolicy: "network-only",
        skip: !accountId,
    });

    const currentRole = roleData?.getAllJobrole?.find((r) => r.id === roleId);
    const safeRoleName = name || currentRole?.name || "";

    useEffect(() => {
        setInputType("Forms");
        setInitialInputType("Forms");
        setIsSaveDisabled(true);
        setIsNextDisabled(true);
    }, []);

    const handleInputChange = (event) => {
        const newValue = event.target.value;

        setInputType(newValue);

        // Whenever user clicks Forms or Numbers,
        // enable Save button.
        setIsSaveDisabled(newValue === initialInputType);

        setIsNextDisabled(false);
    };

    const handleSave = async () => {
        if (!inputType) return;

        setLoading(true);
        setIsSaveDisabled(true);
        setErrorMessage("");

        try {
            const variables = {
                dartsFormNeeded: inputType === "Forms",
                accountId,
                jobroleId: roleId,
                pocName: currentRole?.pocName ?? pocName ?? "",
                pocEmail: currentRole?.pocEmail ?? pocEmail ?? "",
                pocNumber: currentRole?.pocContactNumber ?? pocNumber ?? "",
                numberLogin: currentRole?.numberLogin ?? numberLogin ?? false,
                emailLogin: currentRole?.emailLogin ?? emailLogin ?? false,
                mandatoryTimeDistribution: currentRole?.mandatoryTimeDistribution ?? mandatoryTimeDistribution ?? false,
                targetEditDays: currentRole?.targetEditDays ?? targetEditDays ?? 0,
                inputEditDays: currentRole?.inputEditDays ?? inputEditDays ?? 0,
                name: currentRole?.name ?? name ?? "",
                dartsNeeded: currentRole?.dartsNeeded ?? dartsNeeded ?? false,
                clanNeeded: currentRole?.clanNeeded ?? clanNeeded ?? false,
                syncNeeded: currentRole?.syncNeeded ?? syncNeeded ?? false,
                signInSignOutRequired: currentRole?.signInSignOutRequired ?? signInSignOutRequired ?? false,
                locationRequired: currentRole?.locationRequired ?? locationRequired ?? false,
                geoTracking: currentRole?.geoTracking ?? geoTracking ?? false,
                jobroleAccessUserIds: jobroleAccessUserIds ?? [],
                ldmsNeeded: currentRole?.ldmsNeeded ?? ldmsNeeded ?? false,
                mirrorNeeded: currentRole?.mirrorNeeded ?? mirrorNeeded ?? false,
            };

            const response = await addJobRole({
                variables: {
                    ...variables,
                },
            });

            if (response?.data?.addJobrole?.success) {
                message.success("Jobrole updated successfully");
                setInitialInputType(inputType);
                setIsSaveDisabled(true);
            } else {
                setErrorMessage(response?.data?.addJobrole?.message || "Something went wrong!");
            }
        } catch (err) {
            setErrorMessage("Error occurred while saving data.");
            console.error("Error:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleTargetFunnelNext = (data) => {
        setTargetData(data);
        setCurrentView("preview");
    };

    const handlePreviewBack = () => {
        setCurrentView("targetFunnel");
    };

    const handleMainNavigate = (viewName) => {
        setCurrentView(viewName);
    };

    const handleTargetPreviewNext = (data) => {
        setLeadIndicatorData(data);
        setCurrentView("leadIndicator");
    };

    const handleBack = () => {
        navigate("/admin");
    };

    const handleBackToViewRole = () => {
        navigate("/productsConfiguration", {
            state: {
                roleId: roleId ?? location.state?.roleId,
                accountId: accountId ?? location.state?.accountId,
                accountName: accountName ?? location.state?.accountName,
                name: name ?? location.state?.name,
                dartsNeeded: inputType ? true : (dartsNeeded ?? location.state?.dartsNeeded ?? false),
                clanNeeded: clanNeeded ?? location.state?.clanNeeded ?? false,
                syncNeeded: syncNeeded ?? location.state?.syncNeeded ?? false,
                dartsFormNeeded: inputType ? inputType === "Forms" : (dartsFormNeeded ?? location.state?.dartsFormNeeded ?? false),
                pocName: pocName ?? location.state?.pocName,
                pocEmail: pocEmail ?? location.state?.pocEmail,
                pocNumber: pocNumber ?? location.state?.pocNumber,
                numberLogin: numberLogin ?? location.state?.numberLogin ?? false,
                emailLogin: emailLogin ?? location.state?.emailLogin ?? false,
                mandatoryTimeDistribution: mandatoryTimeDistribution ?? location.state?.mandatoryTimeDistribution ?? false,
                targetEditDays: targetEditDays ?? location.state?.targetEditDays ?? 0,
                inputEditDays: inputEditDays ?? location.state?.inputEditDays ?? 0,
                geoTracking: geoTracking ?? location.state?.geoTracking ?? false,
                signInSignOutRequired: signInSignOutRequired ?? location.state?.signInSignOutRequired ?? false,
                locationRequired: locationRequired ?? location.state?.locationRequired ?? false,
                jobroleAccessUserIds: jobroleAccessUserIds ?? location.state?.jobroleAccessUserIds ?? [],
                ldmsNeeded: ldmsNeeded ?? location.state?.ldmsNeeded ?? false,
                mirrorNeeded: mirrorNeeded ?? location.state?.mirrorNeeded ?? false,
            },
        });
    };

    const handleDiscardClick = () => {
        navigate("/admin");
    };

    const renderView = () => {
        switch (currentView) {
            case "targetFunnel":
                return (
                    <TargetFunnelConfiguration
                        onBack={() => setCurrentView("main")}
                        onNext={handleTargetFunnelNext}
                        roleId={roleId}
                        accountId={accountId}
                    />
                );
            case "preview":
                return (
                    <TargetFunnelPreview
                        onBack={handlePreviewBack}
                        onNext={handleTargetPreviewNext}
                        targetData={targetData}
                        roleId={roleId}
                        accountId={accountId}
                    />
                );
            case "leadIndicator":
                return (
                    <LeadIndicatorSelection
                        onBack={() => setCurrentView("preview")}
                        onSaveSuccess={() => setCurrentView("main")}
                        roleId={leadIndicatorData?.roleId}
                        targetFunnelName={leadIndicatorData?.targetFunnelName}
                    />
                );
            case "main":
            default:
                return (
                  <div className="user-profile-container product-configuration-page" style={{ paddingTop: 105, marginTop: 0 }}>
                        {/* <PageBreadcrumb /> */}

                        <div className="flex justify-between items-center w-full  mx-6 ">

                            {/* Back Button */}
                            <button
                                onClick={handleBackToViewRole}
                                className="flex items-center gap-2 ml-[-1.5rem] bg-[#C21A17] text-white px-5 py-2 rounded-lg text-sm font-semibold shadow-md hover:bg-[#A91513] transition-all duration-200"
                            >
                                <img
                                    src={BackIcon}
                                    alt="Back"
                                    className="w-[15px] h-[15px] object-contain"
                                />
                                Back
                            </button>

                            {/* Home Button */}
                            <button
                                onClick={() => navigate("/admin")}
                                className="flex items-center gap-2 bg-[#C21A17] text-white px-5 py-2 mr-10 rounded-lg text-sm font-semibold shadow-md hover:bg-[#A91513] transition-all duration-200"
                            >
                                <img
                                    src={HomeIcon}
                                    alt="Home"
                                    className="w-[15px] h-[15px] object-contain"
                                />
                                Home
                            </button>

                        </div>
                        <div
                            className="flex items-center text-sm text-[#939393]"
                            style={{ marginTop: 12, marginLeft: 5 }}
                        >
                            <span
                                onClick={() => navigate("/admin")}
                                className="cursor-pointer"
                            >
                                {safeAccountName}
                            </span>

                            <span className="mx-2 text-[14px]">
                                ›
                            </span>

                            <span
                                onClick={() =>
                                    navigate("/dashboard", {
                                        state: {
                                            roleId,
                                            accountId,
                                            name,
                                            accountName,
                                        },
                                    })
                                }
                                className="cursor-pointer"
                            >
                                {safeRoleName}
                            </span>

                            <span className="mx-2 text-[14px]">
                                ›
                            </span>

                            {/* Experience Configuration */}
                            <span
                                onClick={() =>
                                    navigate("/experience-configuration", {
                                        state: {
                                            roleId,
                                            accountId,
                                            name,
                                            accountName,
                                        },
                                    })
                                }
                                className="cursor-pointer"
                            >
                                Experience Configuration
                            </span>

                            <span className="mx-2 text-[14px]">
                                ›
                            </span>

                            {/* Product Configuration */}
                            <span
                                onClick={() =>
                                    navigate("/productsConfiguration", {
                                        state: {
                                            roleId,
                                            accountId,
                                            name,
                                            accountName,
                                        },
                                    })
                                }
                                className="cursor-pointer"
                            >
                                Product Configuration
                            </span>

                            <span className="mx-2 text-[14px]">
                                ›
                            </span>

                            <span className="font-semibold text-gray-800">
                                Clan
                            </span>
                        </div>


                        <div>

                            <div className="w-full flex justify-center my-8">
                                <div className="flex items-center gap-3 p-1.5 bg-white border border-gray-300 rounded-xl shadow-sm">

                                    <button
                                        className="px-5 py-1 rounded-lg text-base font-semibold bg-red-50 text-red-600"
                                    >
                                        Darts
                                    </button>

                                    <button
                                        onClick={() =>
                                            navigate("/faqs", {
                                                state: {
                                                    accountId,
                                                    roleId,
                                                    accountName,
                                                    name,
                                                },
                                            })
                                        }
                                        className="px-5 py-1 rounded-lg text-base font-semibold text-gray-500"
                                    >
                                        FAQ's
                                    </button>

                                    <button
                                        onClick={() =>
                                            navigate("/clan-management", {
                                                state: {
                                                    accountId,
                                                    roleId,
                                                    accountName,
                                                    name,
                                                    activeTab: '1'
                                                },
                                            })
                                        }
                                        className="px-5 py-1 rounded-lg text-base font-semibold text-gray-500"
                                    >
                                        Content
                                    </button>

                                </div>
                            </div>
                            <img
                                src={toggleImage01}
                                className="centered-image"
                                alt="Toggle Image"
                            />
                            <h2 className="product-config-title">Input Type Configuration</h2>

                            <div className="product-config-card">
                                <label className="product-config-option">
                                    <input
                                        type="radio"
                                        style={{ accentColor: "#137C14" }}
                                        name="inputType"
                                        value="Forms"
                                        checked={inputType === "Forms"}
                                        onChange={handleInputChange}
                                    />
                                    <div
                                        className="radio-label"
                                        onClick={() => handleMainNavigate("targetFunnel")}
                                    >
                                        <div>
                                            <strong>Forms</strong>
                                            <p>Structured data input through forms</p>
                                        </div>
                                        <img src={NextIcon} alt="Next" className="next-icon" />
                                    </div>
                                </label>

                                <label className="product-config-option">
                                    <input
                                        type="radio"
                                        style={{ accentColor: "#137C14" }}
                                        name="inputType"
                                        value="Numbers"
                                        checked={inputType === "Numbers"}
                                        onChange={handleInputChange}
                                    />
                                    <div
                                        className="radio-label"
                                        onClick={() => handleMainNavigate("targetFunnel")}
                                    >
                                        <div>
                                            <strong>Numbers</strong>
                                            <p>Simple numerical input forms</p>
                                        </div>
                                        <img src={NextIcon} alt="Next" className="next-icon" />
                                    </div>
                                </label>

                                {/* <label
                                    className="product-config-option"
                                    onClick={() =>
                                        navigate("/attendance-daily-norm", {
                                            state: {
                                                accountId,
                                                roleId,
                                                accountName,
                                                name,
                                                dartsFormNeeded, // Add this to preserve the value
                                                dartsNeeded,
                                                clanNeeded,
                                                pocName,
                                                pocEmail,
                                                pocNumber,
                                                numberLogin,
                                                emailLogin,
                                                mandatoryTimeDistribution,
                                                targetEditDays,
                                                inputEditDays,
                                                signInSignOutRequired,
                                                locationRequired,
                                                ldmsNeeded,
                                                mirrorNeeded,
                                                geoTracking,
                                                jobroleAccessUserIds

                                            },
                                        })
                                    }
                                >
                                    <div className="radio-label">
                                        <div>
                                            <strong>Attendance & Time Distribution</strong>
                                            <p>Simple numerical input forms</p>
                                        </div>
                                        <img src={NextIcon} alt="Next" className="next-icon" />
                                    </div>
                                </label> */}

                                <label
                                    className="product-config-option"
                                    onClick={() =>
                                        navigate("/stages", {
                                            state: { accountId, roleId, accountName, name, dartsFormNeeded },
                                        })
                                    }
                                >
                                    <div className="radio-label">
                                        <div>
                                            <strong>LDMS Configuration</strong>
                                            <p>Define stages, statuses and activities</p>
                                        </div>
                                        <img src={NextIcon} alt="Next" className="next-icon" />
                                    </div>
                                </label>
                            </div>

                            {errorMessage && (
                                <div className="error-message">
                                    <p>{errorMessage}</p>
                                </div>
                            )}

                            <div className="product-config-buttons">
                                <button
                                    className={`product-config-btn ${isSaveDisabled ? "disabled-btn" : "enabled-btn"
                                        }`}
                                    onClick={handleSave}
                                    disabled={isSaveDisabled || loading}
                                >
                                    {loading ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : "Save"}
                                </button>
                            </div>
                        </div>
                    </div >
                );
        }
    };

    return <div className="product-config-container">{renderView()}</div>;
};

export default ProductConfiguration;